import {
  ExpressAccount,
  ExpressAccountRequest,
  ExpressContact,
  ExpressContactKind,
  ExpressContactRequest,
} from "../../types/express-master.type";
import { TAX_ID_LENGTH } from "../CreditDebitNote/noteDraft";

/** The create / edit dialogs' state and the rules over it, kept apart so they can be tested. */

export interface ContactForm {
  code: string;
  kind: ExpressContactKind;
  title: string;
  name: string;
  taxId: string;
  taxBranchNo: string;
  address: string;
  phone: string;
  creditDays: string;
  isActive: boolean;
}

export type ContactFormErrors = Partial<Record<"code" | "name" | "taxId", string>>;

export interface AccountForm {
  code: string;
  name: string;
  isActive: boolean;
}

export type AccountFormErrors = Partial<Record<"code" | "name", string>>;

export const EMPTY_CONTACT_FORM: ContactForm = {
  code: "",
  kind: "SUPPLIER",
  title: "",
  name: "",
  taxId: "",
  taxBranchNo: "0",
  address: "",
  phone: "",
  creditDays: "0",
  isActive: true,
};

export const EMPTY_ACCOUNT_FORM: AccountForm = { code: "", name: "", isActive: true };

/** A code as the server stores it: trimmed and upper-cased, so 303fp00001 and 303FP00001 are one. */
export const normalizeCode = (text: string): string => text.trim().toUpperCase();

const blankToNull = (text: string): string | null => text.trim() || null;

/** A whole number keyed as text; blank counts as 0. */
const toWholeNumber = (text: string): number => (text.trim() === "" ? 0 : Number(text));

export const contactFormFrom = (contact: ExpressContact): ContactForm => ({
  code: contact.code,
  kind: contact.kind,
  title: contact.title ?? "",
  name: contact.name,
  taxId: contact.tax_id ?? "",
  taxBranchNo: String(contact.tax_branch_no),
  address: contact.address ?? "",
  phone: contact.phone ?? "",
  creditDays: String(contact.credit_days),
  isActive: contact.is_active,
});

export const accountFormFrom = (account: ExpressAccount): AccountForm => ({
  code: account.code,
  name: account.name,
  isActive: account.is_active,
});

const codeAndNameErrors = (code: string, name: string): AccountFormErrors => {
  const errors: AccountFormErrors = {};
  if (!normalizeCode(code)) errors.code = "กรุณากรอกรหัส";
  if (!name.trim()) errors.name = "กรุณากรอกชื่อ";
  return errors;
};

export const validateContactForm = (form: ContactForm): ContactFormErrors => {
  const errors: ContactFormErrors = codeAndNameErrors(form.code, form.name);
  const taxId = form.taxId.trim();
  if (taxId && taxId.length !== TAX_ID_LENGTH) {
    errors.taxId = `เลขประจำตัวผู้เสียภาษีต้องเป็นตัวเลข ${TAX_ID_LENGTH} หลัก`;
  }
  return errors;
};

export const validateAccountForm = (form: AccountForm): AccountFormErrors =>
  codeAndNameErrors(form.code, form.name);

export const toContactRequest = (form: ContactForm): ExpressContactRequest => ({
  code: normalizeCode(form.code),
  kind: form.kind,
  title: blankToNull(form.title),
  name: form.name.trim(),
  tax_id: blankToNull(form.taxId),
  tax_branch_no: toWholeNumber(form.taxBranchNo),
  address: blankToNull(form.address),
  phone: blankToNull(form.phone),
  credit_days: toWholeNumber(form.creditDays),
  is_active: form.isActive,
});

export const toAccountRequest = (form: AccountForm): ExpressAccountRequest => ({
  code: normalizeCode(form.code),
  name: form.name.trim(),
  is_active: form.isActive,
});
